import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabaseClient';
import AdminLayout from '../components/AdminLayout';
import ImageCropModal from '../components/ImageCropModal';
import '../components/AdminLayout.css';

function AdminProfile() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [imageToCrop, setImageToCrop] = useState(null);
    const [profileData, setProfileData] = useState({
        email: '',
        fullName: '',
        phone: '',
        profileImage: ''
    });
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    // ProtectedRoute checks authentication and device, but never role — every
    // other admin screen carries this guard and this one did not, so any
    // authenticated user on a desktop could open /admin/profile. Matches the
    // check in AdminSettings.js.
    useEffect(() => {
        if (!user) return;
        const userRole = user?.user_metadata?.role || localStorage.getItem('role');
        if (String(userRole) !== '4') {
            navigate('/welcome');
            return;
        }
        loadAdminProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const loadAdminProfile = async () => {
        try {
            const { data, error } = await supabase
                .from('users')
                .select('email, full_name, phone, profile_image')
                .eq('id', user.id)
                .single();

            if (error) throw error;

            setProfileData({
                email: data.email || user.email,
                fullName: data.full_name || '',
                phone: data.phone || '',
                profileImage: data.profile_image || ''
            });
        } catch (error) {
            console.error('Error loading profile:', error);
        }
    };

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            // Update user profile in database
            const { error: updateError } = await supabase
                .from('users')
                .update({
                    full_name: profileData.fullName,
                    phone: profileData.phone,
                    updated_at: new Date().toISOString()
                })
                .eq('id', user.id);

            if (updateError) throw updateError;

            // Update email in Supabase Auth if changed
            if (profileData.email !== user.email) {
                const { error: emailError } = await supabase.auth.updateUser({
                    email: profileData.email
                });

                if (emailError) throw emailError;

                setMessage({
                    type: 'success',
                    text: 'Profile updated! Please check your new email for verification.'
                });
            } else {
                setMessage({
                    type: 'success',
                    text: 'Profile updated successfully!'
                });
            }

            // Reload profile
            await loadAdminProfile();
        } catch (error) {
            console.error('Error updating profile:', error);
            setMessage({
                type: 'error',
                text: error.message || 'Failed to update profile'
            });
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });

        // Validate passwords
        if (passwordData.newPassword.length < 8) {
            setMessage({
                type: 'error',
                text: 'Password must be at least 8 characters long'
            });
            setLoading(false);
            return;
        }

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setMessage({
                type: 'error',
                text: 'New passwords do not match'
            });
            setLoading(false);
            return;
        }

        // Check password strength
        const hasUpperCase = /[A-Z]/.test(passwordData.newPassword);
        const hasLowerCase = /[a-z]/.test(passwordData.newPassword);
        const hasNumber = /[0-9]/.test(passwordData.newPassword);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(passwordData.newPassword);

        if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
            setMessage({
                type: 'error',
                text: 'Password must contain uppercase, lowercase, number, and special character'
            });
            setLoading(false);
            return;
        }

        try {
            // Update password in Supabase Auth
            const { error } = await supabase.auth.updateUser({
                password: passwordData.newPassword
            });

            if (error) throw error;

            setMessage({
                type: 'success',
                text: 'Password changed successfully!'
            });

            // Clear password fields
            setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
        } catch (error) {
            console.error('Error changing password:', error);
            setMessage({
                type: 'error',
                text: error.message || 'Failed to change password'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setMessage({ type: 'error', text: 'Please select an image file' });
            return;
        }

        // Validate file size (max 5MB before cropping)
        if (file.size > 5 * 1024 * 1024) {
            setMessage({ type: 'error', text: 'Image size must be less than 5MB' });
            return;
        }

        // Open crop modal
        const reader = new FileReader();
        reader.onload = (e) => {
            setImageToCrop(e.target.result);
            setCropModalOpen(true);
        };
        reader.readAsDataURL(file);

        // Reset file input
        e.target.value = '';
    };

    const handleCropComplete = async (croppedImageBlob) => {
        setUploadingImage(true);
        setMessage({ type: '', text: '' });
        setCropModalOpen(false);

        try {
            // Create file path
            const fileExt = 'jpg'; // Always save as JPEG after cropping
            const fileName = `${user.id}-${Date.now()}.${fileExt}`;
            const filePath = `profile-images/${fileName}`;

            // Delete old image if exists
            if (profileData.profileImage) {
                const oldPath = profileData.profileImage.split('/').pop();
                await supabase.storage
                    .from('avatars')
                    .remove([`profile-images/${oldPath}`]);
            }

            // Upload cropped image
            const { error: uploadError } = await supabase.storage
                .from('avatars')
                .upload(filePath, croppedImageBlob, {
                    contentType: 'image/jpeg',
                    cacheControl: '3600',
                    upsert: false
                });

            if (uploadError) throw uploadError;

            // Get public URL
            const { data: { publicUrl } } = supabase.storage
                .from('avatars')
                .getPublicUrl(filePath);

            // Update database
            const { error: updateError } = await supabase
                .from('users')
                .update({ profile_image: publicUrl })
                .eq('id', user.id);

            if (updateError) throw updateError;

            setProfileData({ ...profileData, profileImage: publicUrl });
            setMessage({ type: 'success', text: 'Profile image updated successfully!' });
            
            // Notify header to update avatar
            window.dispatchEvent(new Event('profile-image-updated'));
        } catch (error) {
            console.error('Error uploading image:', error);
            setMessage({ type: 'error', text: error.message || 'Failed to upload image' });
        } finally {
            setUploadingImage(false);
            setImageToCrop(null);
        }
    };

    const handleCropCancel = () => {
        setCropModalOpen(false);
        setImageToCrop(null);
    };

    return (
        <AdminLayout>
            {cropModalOpen && imageToCrop && (
                <ImageCropModal 
                    imageUrl={imageToCrop}
                    onCropComplete={handleCropComplete}
                    onCancel={handleCropCancel}
                />
            )}
            
            <div className="page-header">
                <div>
                    <h1 className="page-title">
                        <i className="bx bx-user-circle"></i>
                        Profile Settings
                    </h1>
                    <p className="page-subtitle">Manage your admin account information</p>
                </div>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type}`} style={{
                    padding: '1rem',
                    borderRadius: '0.5rem',
                    marginBottom: '1.5rem',
                    background: message.type === 'success' ? '#d1fae5' : '#fee2e2',
                    color: message.type === 'success' ? '#065f46' : '#991b1b',
                    border: `1px solid ${message.type === 'success' ? '#10b981' : '#ef4444'}`
                }}>
                    {message.type === 'success' ? '✅ ' : '❌ '}{message.text}
                </div>
            )}

            {/* Profile Image Section */}
            <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
                <div className="card-header">
                    <h3 className="card-title">Profile Picture</h3>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
                    <div style={{ position: 'relative' }}>
                        {profileData.profileImage ? (
                            <img 
                                src={profileData.profileImage} 
                                alt="Profile" 
                                style={{
                                    width: '120px',
                                    height: '120px',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    border: '4px solid #e4e6ef'
                                }}
                            />
                        ) : (
                            <div style={{
                                width: '120px',
                                height: '120px',
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontSize: '3rem',
                                border: '4px solid #e4e6ef'
                            }}>
                                <i className='bx bx-user'></i>
                            </div>
                        )}
                    </div>

                    <div style={{ flex: 1 }}>
                        <h4 style={{ margin: '0 0 0.5rem 0', color: '#181c32' }}>{profileData.fullName || 'Administrator'}</h4>
                        <p style={{ margin: '0 0 1rem 0', color: '#a1a5b7' }}>{profileData.email}</p>
                        
                        <input 
                            type="file" 
                            id="profile-image-upload" 
                            accept="image/*" 
                            onChange={handleImageUpload}
                            style={{ display: 'none' }}
                        />
                        <label htmlFor="profile-image-upload" className="btn btn-primary" style={{ cursor: 'pointer' }}>
                            <i className='bx bx-upload'></i>
                            {uploadingImage ? 'Uploading...' : 'Upload New Photo'}
                        </label>
                        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: '#a1a5b7' }}>
                            Max size: 5MB. Supported formats: JPG, PNG, GIF
                        </p>
                    </div>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                {/* Profile Information */}
                <div className="admin-card">
                    <div className="card-header">
                        <h3 className="card-title">Profile Information</h3>
                    </div>

                    <form onSubmit={handleProfileUpdate}>
                        <div className="form-group">
                            <label className="form-label">Email Address</label>
                            <input
                                type="email"
                                className="form-control"
                                value={profileData.email}
                                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                                required
                            />
                            <small className="form-text">Changing email requires verification</small>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Full Name</label>
                            <input
                                type="text"
                                className="form-control"
                                value={profileData.fullName}
                                onChange={(e) => setProfileData({ ...profileData, fullName: e.target.value })}
                                placeholder="Enter your full name"
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Phone Number</label>
                            <input
                                type="tel"
                                className="form-control"
                                value={profileData.phone}
                                onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                                placeholder="+1 234 567 8900"
                            />
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            <i className='bx bx-save'></i>
                            {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                    </form>
                </div>

                {/* Change Password */}
                <div className="admin-card">
                    <div className="card-header">
                        <h3 className="card-title">Change Password</h3>
                    </div>

                    <form onSubmit={handlePasswordChange}>
                        <div className="form-group">
                            <label className="form-label">New Password</label>
                            <input
                                type="password"
                                className="form-control"
                                value={passwordData.newPassword}
                                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                placeholder="Enter new password"
                                required
                            />
                            <small className="form-text">
                                Must be 8+ characters with uppercase, lowercase, number, and special character
                            </small>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Confirm New Password</label>
                            <input
                                type="password"
                                className="form-control"
                                value={passwordData.confirmPassword}
                                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                                placeholder="Confirm new password"
                                required
                            />
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            <i className='bx bx-lock'></i>
                            {loading ? 'Changing...' : 'Change Password'}
                        </button>
                    </form>
                </div>
            </div>
        </AdminLayout>
    );
}

export default AdminProfile;
